import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Button } from '@/components/common/Buttons';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Image } from '@/components/common/image';
import { Heading, HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { PortableText } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { getButtonComponentThemeFlat } from '@/lib/colourUtils';
import { blockUrlWithAudience } from '@/lib/utils';

import type { SanityAudienceType, SanityUspType } from '@/types/sanity';
import type { BlockWithAudience } from '@/types/shared';

export interface UspProps
  extends Omit<SanityUspType, 'audienceRef'>,
    BlockWithAudience {
  audienceRef?: SanityAudienceType;
  className: string;
}

export const Usp = ({
  heading,
  uspItems,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,
  ctaHeading,
  ctaBody,
  ctaLabel,
  ctaUrl,
  trackingTag,

  audienceRef,
  noAudienceToggle,
  pageAudience,
  isAudienceSwitcherEnabled,
}: UspProps) => {
  const backgroundColourToken = backgroundColour?.token;

  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColourToken}
      className="usp"
    >
      <UspContainer>
        <HeadingLevel>
          <CaptionHeadingDescription heading={heading} />
          {uspItems.map((item) => (
            <UspItem key={item._key}>
              <HeadingLevel>
                <ImageContainer>
                  <Image
                    {...item.featureImage}
                    sizes="(max-width: 976px) 100vw, (max-width: 1440px) 50vw, 720px"
                    radius="l"
                  />
                </ImageContainer>
                <CopyContainer>
                  <Heading size="heading-md">{item.heading}</Heading>
                  <PortableText
                    value={item.text}
                    audienceConfig={{ pageAudience, isAudienceSwitcherEnabled }}
                  />
                </CopyContainer>
              </HeadingLevel>
            </UspItem>
          ))}
          <CTASection>
            <CaptionHeadingDescription
              heading={ctaHeading}
              description={ctaBody}
              desktopAlignment="center"
              mobileAlignment="center"
              pageAudience={pageAudience}
              isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
            />
            {!!ctaUrl && !!ctaLabel && (
              <StyledButton
                variant={getButtonComponentThemeFlat(backgroundColourToken)}
                href={blockUrlWithAudience(
                  ctaUrl,
                  audienceRef,
                  pageAudience,
                  noAudienceToggle,
                  isAudienceSwitcherEnabled,
                )}
                className={
                  trackingTag?.blockName &&
                  `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
                }
              >
                {ctaLabel}
              </StyledButton>
            )}
          </CTASection>
        </HeadingLevel>
      </UspContainer>
    </Section>
  );
};

const UspContainer = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-large);

  h2 {
    text-align: center;
    margin-bottom: var(--spacing-x-large);
  }
`;

const UspItem = styled.div`
  position: relative;
  width: 100%;

  ${Heading} {
    padding: 24px 0;
  }

  ${screen.md} {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    ${Heading} {
      padding: 0 0 var(--spacing-300) 0;
    }

    &:nth-of-type(even) {
      flex-direction: row-reverse;
    }
  }

  & + & {
    margin-top: var(--spacing-xxx-large);
  }
`;

const CopyContainer = styled.div`
  --column-padding: 20px;

  ${screen.md} {
    --column-padding: 30px;
  }
  ${screen.xl} {
    --column-padding: 40px;
  }

  ${screen.md} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0px var(--column-padding);
    flex: 1 0 50%;
  }

  ul {
    list-style: initial;
    padding: 0 24px;
    li {
      padding: 8px 0;
    }
  }

  ${Paragraph} {
    margin-bottom: 12px;
  }
`;

const ImageContainer = styled.div`
  ${screen.md} {
    flex: 1 0 50%;
  }
`;

const CTASection = styled.div`
  text-align: center;
  ${Heading} {
    padding: 0 0 var(--spacing-300) 0;
  }
  ${Paragraph} {
    margin-bottom: var(--spacing-500);
  }
`;

const StyledButton = styled(Button)`
  margin: var(--spacing-x-large) auto 0;
`;
